import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Briefcase,
  ShoppingCart,
  Server,
  MessageSquare,
  Bot,
  Zap,
  ArrowRight,
  Star,
  TrendingUp,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { createSlug } from '../lib/utils';
import { API_URLS, API_CONFIG } from '../config/api';
import MediaDisplay from './MediaDisplay';

const ServicesSection = ({ sectionContent }) => {
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Icon mapping
  const iconMap = {
    'Briefcase': Briefcase,
    'ShoppingCart': ShoppingCart,
    'Server': Server,
    'MessageSquare': MessageSquare,
    'Bot': Bot,
    'Zap': Zap,
    'ArrowRight': ArrowRight,
    'Star': Star,
    'TrendingUp': TrendingUp,
    'Shield': Shield
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(API_URLS.SERVICES);
        // Handle both new format {status: 'success', data: [...]} and old format [...]
        const servicesData = response.data.data || response.data;
        if (servicesData && Array.isArray(servicesData)) {
          setServices(servicesData);
        } else {
          setServices([]);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Hizmetler yüklenirken bir hata oluştu');
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const getBadgeVariant = (badge) => {
    switch (badge) {
      case 'Popüler': return 'default';
      case 'Yeni': return 'secondary';
      case 'Güvenilir': return 'outline';
      case 'Hızlı': return 'destructive';
      case 'AI': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <section id="hizmetler" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AFF] mx-auto"></div>
            <p className="mt-4 text-gray-600">Hizmetler yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Tekrar Dene
            </Button>
          </div>
        ) : services && services.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text Content and Cards */}
            <div className="space-y-8">
              {/* Text Content */}
              <div className="space-y-6 text-center md:text-left">
                <div className="inline-flex items-center space-x-2 bg-[#7A5AFF]/15 text-[#7A5AFF] px-6 py-3 rounded-full text-sm font-medium transform hover:scale-105 transition-transform duration-300">
                  <Zap className="w-5 h-5 animate-bounce" />
                  <span>{sectionContent?.badge_text || 'Hizmetlerimiz'}</span>
                </div>

                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  {sectionContent?.title || 'Hizmetlerimiz'}
                </h2>

                <p className="text-xl text-gray-600 leading-relaxed">
                  {sectionContent?.description || 'E-ticaret, web tasarım, mobil uygulama geliştirme ve yapay zeka çözümleri ile işinizi dijitale taşıyoruz. Uzman ekibimizle hayalinizdeki projeyi gerçeğe dönüştürün.'}
                </p>
              </div>

              {/* Service Cards - 2x2 Grid */}
              <div className="grid grid-cols-2 gap-6">
                {services.slice(0, 4).map((service, index) => {
                  const iconName = service.icon;
                  const IconComponent = (typeof iconName === 'string' && iconMap[iconName]) ? iconMap[iconName] : Briefcase;
                  const serviceKey = service.id || service.title || `service-${index}`;
                  const serviceColor = service.color || 'from-[#7A5AFF] to-[#5a3fd9]';
                  
                  return (
                    <Link
                      key={serviceKey}
                      to={`/hizmet/${service.slug || createSlug(service.title || service.name || 'default')}`}
                      className="block"
                    >
                      <Card className="group relative overflow-hidden bg-white border-0 transform hover:scale-105 transition-all duration-500 hover:shadow-2xl cursor-pointer h-full">
                        {/* Badge */}
                        {service.badge && (
                          <div className="absolute top-3 right-3 z-10">
                            <Badge variant={getBadgeVariant(service.badge)} className="text-xs font-medium">
                              {service.badge}
                            </Badge>
                          </div>
                        )}

                        {/* Animated Background Gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${serviceColor} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                        <CardContent className="p-6 space-y-4 h-full flex flex-col">
                          {/* Icon */}
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${serviceColor} flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                            {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                          </div>

                          {/* Service Info */}
                          <div className="space-y-2 flex-grow">
                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#7A5AFF] transition-colors duration-300">
                              {service.title}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                              {service.description}
                            </p>
                          </div>

                          {/* View Button */}
                          <div className="flex items-center justify-between text-[#7A5AFF] group-hover:text-[#5a3fd9] font-medium text-sm">
                            <span>Detayları Gör</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>

              {/* Tüm Hizmetler Button */}
              <div className="pt-4 flex justify-center md:justify-start">
                <Link to={sectionContent?.button_link || '/hizmetler'}>
                  <Button size="lg" className="bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6] text-white transform hover:scale-105 transition-all duration-300 text-lg px-8 py-6 shadow-lg shadow-[#7A5AFF]/30">
                    <Briefcase className="w-5 h-5 mr-2" />
                    {sectionContent?.button_text || 'Tüm Hizmetleri Görüntüle'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="hidden lg:flex justify-center items-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute inset-0 bg-gradient-to-r from-[#7A5AFF]/20 to-[#7A5AFF]/5 rounded-3xl blur-3xl"></div>
                <MediaDisplay
                  src={sectionContent?.image_url || '/website-al-logo.png'}
                  fallbackSrc="/website-al-logo.png"
                  alt="Hizmetlerimiz"
                  className="relative w-full h-auto object-contain drop-shadow-2xl rounded-3xl"
                  imgProps={{
                    loading: "lazy"
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">Henüz hizmet bulunmuyor.</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Tekrar Dene
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;